import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {AlertTriangle, CheckCircle2, Calendar, ArrowRight, Users, Baby, Heart, ShieldCheck} from 'lucide-react';
import BookingModal from '../components/BookingModal';

const FamilyTravel = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const healthRisks = [
    {
      icon: <Baby className="w-6 h-6" />,
      title: "Pediatric Vaccine Schedules",
      desc: "Children have different vaccine timing and dosing requirements than adults."
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Pregnancy Considerations",
      desc: "Some vaccines and destinations pose risks during pregnancy."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Multi-Age Coordination",
      desc: "Different family members need different health preparations and precautions."
    }
  ];

  const pediatricVaccines = [
    { name: "Routine Childhood Vaccines", priority: "Ensure Up-to-Date", note: "MMR, varicella, DTaP, polio per Canadian schedule" },
    { name: "Hepatitis A (Pediatric)", priority: "Highly Recommended", note: "Available from age 1; two doses 6-12 months apart" },
    { name: "Typhoid", priority: "Age 2+", note: "Injectable from age 2; oral capsules from age 6" },
    { name: "Yellow Fever", priority: "Age 9 months+", note: "Required for some countries; not given before 9 months" },
    { name: "Japanese Encephalitis", priority: "Age 2 months+", note: "Consider for rural Asia travel with children" },
    { name: "Influenza", priority: "Age 6 months+", note: "Annual flu shot, especially for travel during flu season" },
    { name: "Rabies (Pre-Exposure)", priority: "Consider for Adventure", note: "Available for children; important for animal contact" },
    { name: "Meningococcal", priority: "Destination-Specific", note: "Different formulations available for various ages" }
  ];

  const pregnancyConsiderations = [
    "Yellow fever, MMR, varicella are contraindicated during pregnancy",
    "Some destinations with Zika risk are not recommended for pregnant travellers",
    "Malaria prophylaxis options are limited during pregnancy (consult specialist)",
    "Travel during second trimester (14-28 weeks) is generally safest",
    "Air travel typically permitted until 36 weeks for uncomplicated pregnancies",
    "Ensure destination has adequate medical facilities for obstetric care"
  ];

  const familyHealthTips = [
    {
      icon: <Baby className="w-5 h-5" />,
      tip: "Pack Pediatric Medications",
      detail: "Bring child-safe pain relievers, anti-diarrheal for older kids, and rehydration solutions."
    },
    {
      icon: <ShieldCheck className="w-5 h-5" />,
      tip: "Sun Protection for Kids",
      detail: "Use SPF 50+ sunscreen, protective clothing, hats. Avoid midday sun exposure."
    },
    {
      icon: <Heart className="w-5 h-5" />,
      tip: "Food Safety First",
      detail: "Be extra cautious with food/water for young children. Avoid street food and ice."
    },
    {
      icon: <Users className="w-5 h-5" />,
      tip: "Travel Insurance for All",
      detail: "Ensure family coverage includes pediatric emergency and evacuation care."
    }
  ];

  const ageGroups = [
    {
      age: "Young Children (4-11 years)",
      considerations: [
        "Most travel vaccines available",
        "Extra supervision for hygiene and safety",
        "Child-friendly malaria prophylaxis options",
        "Consider shorter trips to ease adaptation"
      ]
    },
    {
      age: "Teens (12-17 years)",
      considerations: [
        "Similar vaccine schedule to adults",
        "Meningococcal vaccine important for this age group",
        "HPV vaccine if not yet completed",
        "Mental health and travel anxiety support"
      ]
    },
    {
      age: "Pregnant Travellers",
      considerations: [
        "Avoid live vaccines (MMR, varicella, yellow fever)",
        "Zika-risk destinations require special consideration",
        "Second trimester safest for travel",
        "Choose destinations with good medical facilities"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-navy-900">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-rose-500 via-pink-600 to-purple-700 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10" />
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/1271619/pexels-photo-1271619.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Family Travel"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl"
          >
            <div className="inline-block px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white text-sm font-medium mb-6">
              Family Travel • Children • Pregnancy • Multi-Generational
            </div>
            <h1 className="text-4xl md:text-6xl font-heading font-bold text-white mb-6">
              Family & Multi-Age Travel Health
            </h1>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Comprehensive health planning for the whole family. From infants to grandparents, we'll help everyone travel safely together.
            </p>
            <button
              onClick={() => setIsBookingModalOpen(true)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-pink-600 text-lg font-bold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
            >
              Book Family Travel Consultation
              <ArrowRight size={20} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Family Group Pricing Teaser */}
      <section className="py-12 bg-white dark:bg-navy-900 border-b border-gray-200 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-900/10 dark:to-purple-900/10 rounded-2xl p-8 border border-pink-200 dark:border-pink-800">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex-1">
                <h3 className="text-2xl font-heading font-bold text-navy-900 dark:text-white mb-2">
                  Family & Group Consultation Pricing
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Individual consultation: <strong>$60 CAD</strong>. Discounted rates available for families of 3+ members. Ask about multi-person consultations during booking.
                </p>
              </div>
              <button
                onClick={() => setIsBookingModalOpen(true)}
                className="shrink-0 px-8 py-3 bg-pink-600 hover:bg-pink-700 text-white font-bold rounded-full shadow-lg transition-all"
              >
                Book Family Consult
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Age-Specific Considerations */}
      <section className="py-20 bg-gray-50 dark:bg-navy-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy-900 dark:text-white mb-4">
              Health Planning by Age Group
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Every family member has unique health needs. We provide age-appropriate guidance for everyone.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {ageGroups.map((group, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 bg-white dark:bg-navy-900 rounded-2xl shadow-lg border border-gray-100 dark:border-white/5"
              >
                <h3 className="text-xl font-bold text-navy-900 dark:text-white mb-4">{group.age}</h3>
                <ul className="space-y-2">
                  {group.considerations.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-400">
                      <CheckCircle2 className="w-4 h-4 text-pink-600 dark:text-pink-400 shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Health Risks */}
      <section className="py-20 bg-white dark:bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy-900 dark:text-white mb-4">
              Key Considerations for Family Travel
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Traveling with children and pregnant family members requires extra planning and precautions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {healthRisks.map((risk, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 bg-gray-50 dark:bg-navy-800 rounded-2xl border border-gray-100 dark:border-white/5"
              >
                <div className="w-12 h-12 bg-pink-100 dark:bg-pink-900/20 rounded-xl flex items-center justify-center text-pink-600 dark:text-pink-400 mb-4">
                  {risk.icon}
                </div>
                <h3 className="text-xl font-bold text-navy-900 dark:text-white mb-3">{risk.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{risk.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vaccines & Pregnancy Info */}
      <section className="py-20 bg-gray-50 dark:bg-navy-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Pediatric Vaccines */}
            <div>
              <h2 className="text-3xl font-heading font-bold text-navy-900 dark:text-white mb-6">
                Pediatric Vaccines to Discuss
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Children can safely receive most travel vaccines with age-appropriate dosing.
              </p>
              <div className="space-y-4">
                {pediatricVaccines.map((vaccine, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="p-4 bg-white dark:bg-navy-900 rounded-xl border border-gray-100 dark:border-white/5 hover:border-cyan-500/30 transition-all"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-bold text-navy-900 dark:text-white">{vaccine.name}</h4>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ml-2 ${
                        vaccine.priority.includes("Highly")
                          ? "bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400"
                          : vaccine.priority.includes("Age")
                          ? "bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                      }`}>
                        {vaccine.priority}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{vaccine.note}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Pregnancy Considerations */}
            <div>
              <h2 className="text-3xl font-heading font-bold text-navy-900 dark:text-white mb-6">
                Pregnancy & Travel
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Special considerations for pregnant travellers and expecting families:
              </p>
              <ul className="space-y-3 mb-8">
                {pregnancyConsiderations.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-start gap-3"
                  >
                    <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400 shrink-0 mt-0.5" />
                    <span className="text-gray-700 dark:text-gray-300">{item}</span>
                  </motion.li>
                ))}
              </ul>

              <div className="p-4 bg-pink-50 dark:bg-pink-900/10 border border-pink-200 dark:border-pink-800 rounded-xl">
                <div className="flex gap-3">
                  <Heart className="w-5 h-5 text-pink-600 dark:text-pink-400 shrink-0 mt-0.5" />
                  <div className="text-sm text-pink-900 dark:text-pink-100">
                    <strong>Important:</strong> Pregnant travellers should consult with both their obstetrician and a travel medicine specialist before international travel.
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-bold text-navy-900 dark:text-white mb-4 mt-8">Family Health Tips</h3>
              <div className="space-y-3">
                {familyHealthTips.map((tip, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-white dark:bg-navy-900 rounded-lg">
                    <div className="w-8 h-8 bg-cyan-100 dark:bg-cyan-900/20 rounded-lg flex items-center justify-center text-cyan-600 dark:text-cyan-400 shrink-0">
                      {tip.icon}
                    </div>
                    <div>
                      <div className="font-bold text-navy-900 dark:text-white text-sm">{tip.tip}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{tip.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* When to Book */}
      <section className="py-20 bg-gradient-to-br from-navy-900 to-navy-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Calendar className="w-16 h-16 text-cyan-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              When to Book Your Family Consultation
            </h2>
            <p className="text-gray-300 text-lg">
              Families need extra planning time to coordinate everyone's health needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl text-center">
              <div className="text-4xl font-bold text-cyan-400 mb-2">8-12 Weeks</div>
              <div className="text-white font-medium mb-2">Ideal for Families</div>
              <div className="text-sm text-gray-400">Allows time for multi-dose pediatric vaccines</div>
            </div>
            <div className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl text-center">
              <div className="text-4xl font-bold text-pink-400 mb-2">6 Weeks</div>
              <div className="text-white font-medium mb-2">Minimum Recommended</div>
              <div className="text-sm text-gray-400">Time for most vaccines and preparation</div>
            </div>
            <div className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl text-center">
              <div className="text-4xl font-bold text-purple-400 mb-2">Pregnancy</div>
              <div className="text-white font-medium mb-2">Book Early</div>
              <div className="text-sm text-gray-400">Ideally in first or early second trimester</div>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => setIsBookingModalOpen(true)}
              className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-lg font-bold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
            >
              Book Your Family Travel Consultation
              <ArrowRight size={20} />
            </button>
            <p className="text-gray-400 text-sm mt-4">
              Family consultations available • Group discounts for 3+ • Pediatric specialists
            </p>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      <BookingModal 
        isOpen={isBookingModalOpen} 
        onClose={() => setIsBookingModalOpen(false)} 
      />
    </div>
  );
};

export default FamilyTravel;
